import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

// Editable checklist: rename, mark required, reorder (up/down), remove, add.
export const ChecklistEditor = ({ items, onChange, testPrefix = 'checklist' }) => {
  const [draft, setDraft] = useState('');
  const set = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const add = () => { if (!draft.trim()) return; onChange([...items, { title: draft.trim(), is_required: false }]); setDraft(''); };
  return (
    <div className="space-y-1.5" data-testid={`${testPrefix}-editor`}>
      {items.map((it, i) => (
        <div key={it.id || `n-${i}`} className="flex items-center gap-1.5" data-testid={`${testPrefix}-item-${i}`}>
          <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
          <Input className="h-8 text-sm" value={it.title} onChange={(e) => set(i, { title: e.target.value })} data-testid={`${testPrefix}-title-${i}`} />
          <label className="flex items-center gap-1 text-[11px] text-muted-foreground whitespace-nowrap" title="Required">
            <Checkbox checked={!!it.is_required} onCheckedChange={(v) => set(i, { is_required: !!v })} data-testid={`${testPrefix}-required-${i}`} />Req
          </label>
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`${testPrefix}-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`${testPrefix}-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`${testPrefix}-remove-${i}`}><X className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <div className="flex items-center gap-1.5 pl-6">
        <Input className="h-8 text-sm" value={draft} placeholder="Add checklist item" onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid={`${testPrefix}-new-input`} />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={add} data-testid={`${testPrefix}-add-btn`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
      </div>
    </div>
  );
};

export const TaskProgress = ({ task, className }) => {
  const total = task.checklist_total ?? (task.checklist || []).length;
  if (!total) return null;
  const done = task.checklist_done ?? (task.checklist || []).filter((c) => c.done).length;
  return (
    <div className={cn('flex items-center gap-2', className)} data-testid={`task-progress-${task.id}`}>
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden min-w-[48px]"><div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${(done / total) * 100}%` }} /></div>
      <span className="text-[11px] text-muted-foreground tabular-nums">{done}/{total}</span>
    </div>
  );
};
